/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-wrap-multilines */
import { useTrackingTag } from '@dmm-com/react-tracking-tag-api';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';
import {
  Dispatch,
  FC,
  lazy,
  SetStateAction,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

import ErrorModal from '@/components/layouts/Modal/Error';
import Toaster from '@/components/layouts/Toaster';
import { useUserContext } from '@/contexts/user';
import useModal from '@/hooks/modal/useModal';
import useBetPanel from '@/hooks/race/useBetPanel/Singlerace';
import useScript, { Status } from '@/hooks/useScript';
import {
  BetType,
  EntryRacer,
  EntryRacerNote,
  EntryRacerScoreAttByCond,
  EntryRacerScoreDetailOwn,
  OrderSingleRace,
  OrderSingleRaceDetail,
  OrderSingleRaceInput,
  OrderStatus,
  Race,
  RaceEntryStatus,
  RaceProgram,
  ReleaseStatus,
  useOrderSingleRaceMutation,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { sendI3ToretaUseEvent } from '@/lib/dmm/trackingTag';
import {
  filterInvalidBetData,
  formatPurchaseProductDetail,
  getVelodromeByCode,
  makeBettableList,
} from '@/lib/race';
import { OrderData, parsedOrderData } from '@/lib/race/order';
import {
  BetCountData,
  BetData,
  BetDataList,
  BetDetail,
  BetMethod,
  BettableData,
  BettableItem,
  BetTypeList,
  FirstThreeRacerData,
  FirstThreeRacerList,
  IsMultiData,
  OddsData,
  OddsListType,
  OrderStep,
  RaceInfoType,
  Ticket,
  Velodrome,
  WheelAxis,
  WheelAxisList,
} from '@/modelTypes';
import { raceSelector } from '@/selectors/race';
import { ViewedGuestModalSessionStorage } from '@/storages/nav/fromData';

const GuestModal = lazy(() => import('@/components/layouts/Modal/Guest'));
const SingleraceOverDeadlineWatcher = lazy(
  () => import('@/components/layouts/Modal/OverDeadline/Singlerace'),
);
const BetPanelModal = lazy(
  () => import('@/components/layouts/Modal/BetPanel/Singlerace'),
);
const EntryRacerNoteModal = lazy(
  () => import('@/components/layouts/Modal/EntryRacerNote'),
);
const SpLayout = lazy(() => import('./sp'));
const PcLayout = lazy(() => import('./pc'));

interface RaceTemplateProps {
  race: Race;
  raceProgram: RaceProgram;
  racePrograms: RaceProgram[];
  raceInfoType: RaceInfoType;
  hasInfoType: boolean;
  betMethod: BetMethod;
  oddsData: OddsData;
  oddsQueryRefetch: () => void;
  oddsUpdateTime: dayjs.Dayjs;
  setRaceInfoType: Dispatch<SetStateAction<RaceInfoType>>;
  setBetMethod: Dispatch<SetStateAction<BetMethod>>;
  walletQueryRefetch?: () => void;
  raceQueryRefetch: () => void;
  entryRacerNotes: EntryRacerNote[] | undefined;
  orderSingleraces: OrderSingleRace[];
  orderSingleracesRefetch: () => void;
  getScoreDetailOwn: (
    argRaceId: string,
    argHeldDate: string,
    argLimitHeldDate: string,
    argVelodromeCode: number,
  ) => void;
  getRacerScoreByAttributes: (argRaceId: string) => void;
  scoreDetailOwns?: EntryRacerScoreDetailOwn[];
  scoreDetailMatchByBasic?: EntryRacerScoreDetailOwn[];
  scoreDetailMatchByLine?: EntryRacerScoreDetailOwn[];
  scoreByAttribute?: EntryRacerScoreAttByCond[];
}

export interface LayoutProps extends RaceTemplateProps {
  isBettable: boolean;
  betDataList: BetDataList;
  betPanelTicketCount: number;
  oddsListType: OddsListType;
  bettableData: BettableData;
  firstThreeRacerData: FirstThreeRacerData;
  betCountData: BetCountData;
  betTypeList: BetTypeList;
  wheelAxisList: WheelAxisList;
  isMultiData: IsMultiData;
  modifyBetDetailKey: string;
  isBetting: boolean;
  isCanceled: boolean;
  entryRacerNoteList: EntryRacerNote[];
  targetEntryRacer: EntryRacer | null;
  isFirstDay: boolean;
  isNotUnregisteredRaces: boolean;
  isOpenBetPanel: boolean;
  velodrome: Velodrome;
  orderSingleraceData: OrderData;
  hasOrderTicket: boolean;
  isOrderProcessing: boolean;
  addToBetPanel: () => void;
  cancelModifyTheBetDetail: () => void;
  modifyTheBetDetail: (betDetail: BetDetail, key: string) => void;
  isModifyingDisabled: (
    key: 'betMethod' | 'betType' | 'wheelAxis',
    arg: string,
  ) => boolean;
  setIsOpenBetPanel: (bool: boolean) => void;
  setOddsListType: Dispatch<SetStateAction<OddsListType>>;
  setBettableData: Dispatch<SetStateAction<BettableData>>;
  setBetDataList: Dispatch<SetStateAction<BetDataList>>;
  setBetCountData: Dispatch<SetStateAction<BetCountData>>;
  setFirstThreeRacerData: Dispatch<SetStateAction<FirstThreeRacerData>>;
  setBetTypeList: Dispatch<SetStateAction<BetTypeList>>;
  setWheelAxisList: Dispatch<SetStateAction<WheelAxisList>>;
  setIsMultiData: Dispatch<SetStateAction<IsMultiData>>;
  onOrderClosed: () => void;
  setIsBetting: Dispatch<SetStateAction<boolean>>;
  setEntryRacerNoteList: Dispatch<SetStateAction<EntryRacerNote[]>>;
  showEntryRacerNoteModal: (entryRacer: EntryRacer) => void;
  getScoreDetailOwn: (
    argRaceId: string,
    argHeldDate: string,
    argLimitHeldDate: string,
    argVelodromeCode: number,
  ) => void;
  getRacerScoreByAttributes: (argRaceId: string) => void;
}

function initBetMethodData<T>(val: T) {
  const obj = {} as { [key in BetMethod]: T };
  Object.entries(BetMethod).forEach(([_, type]) => {
    // MEMO: 深くネストしたオブジェクトでも参照が別れるようにディープコピーしている
    obj[type] = cloneDeep(val);
  });
  return obj;
}

function initBetTypeData<T>(val: T) {
  const obj = {} as { [key in BetType]: T };
  Object.entries(BetType).forEach(([_, type]) => {
    obj[type] = cloneDeep(val);
  });
  return obj;
}

const initWheelRacerList: { [key in WheelAxis]: FirstThreeRacerList } = {
  [WheelAxis.First]: [[], [], []],
  [WheelAxis.Second]: [[], [], []],
  [WheelAxis.Third]: [[], [], []],
  [WheelAxis.FirstSecond]: [[], [], []],
  [WheelAxis.FirstThird]: [[], [], []],
  [WheelAxis.SecondThird]: [[], [], []],
  [WheelAxis.One]: [[], [], []],
  [WheelAxis.Two]: [[], [], []],
};

const initIsBettable = (race: Race, raceProgram: RaceProgram) =>
  dayjst().isBefore(raceSelector(race).marginedOrderDeadline) &&
  !raceProgram.isCanceled &&
  !race.isCanceled &&
  !raceProgram.isSalesSuspended;

const initBetTypeListData = () => {
  return {
    [BetMethod.Formation]: [BetType.Trifecta],
    [BetMethod.Box]: [BetType.Trifecta],
    [BetMethod.Wheel]: BetType.Trifecta,
    [BetMethod.Odds]: BetType.Trifecta,
  } as BetTypeList;
};

const initFirstThreeRacerData = () => {
  return {
    [BetMethod.Formation]: [[], [], []],
    [BetMethod.Box]: [[], [], []],
    [BetMethod.Wheel]: initBetTypeData(initWheelRacerList),
  } as FirstThreeRacerData;
};

const initWheelAxisList = () => {
  return {
    [BetType.BracketQuinella]: WheelAxis.One,
    [BetType.Quinella]: WheelAxis.One,
    [BetType.BracketPerfecta]: WheelAxis.First,
    [BetType.Perfecta]: WheelAxis.First,
    [BetType.QuinellaPlace]: WheelAxis.One,
    [BetType.Trio]: WheelAxis.One,
    [BetType.Trifecta]: WheelAxis.First,
  } as WheelAxisList;
};

const RaceTemplate: FC<RaceTemplateProps> = props => {
  const {
    race,
    raceProgram,
    racePrograms,
    betMethod,
    setBetMethod,
    raceInfoType,
    hasInfoType,
    setRaceInfoType,
    entryRacerNotes,
    orderSingleraces,
    oddsData,
    oddsQueryRefetch,
    raceQueryRefetch,
    walletQueryRefetch,
    orderSingleracesRefetch,
  } = props;

  const { isGuest } = useUserContext();
  const { serviceCode } = useTrackingTag();
  const [betDataList, setBetDataList] = useBetPanel();
  const [betPanelTicketCount, setBetPanelTicketCount] = useState<number>(0);
  const [modifyBetDetailKey, setModifyBetDetailKey] = useState<string>('');
  const [oddsListType, setOddsListType] = useState<OddsListType>(
    OddsListType.Table,
  );
  const [bettableData, setBettableData] = useState<BettableData>(
    initBetMethodData(initBetTypeData<BettableItem[]>([])),
  );
  const [firstThreeRacerData, setFirstThreeRacerData] =
    useState<FirstThreeRacerData>(initFirstThreeRacerData());
  const [betCountData, setBetCountData] = useState<BetCountData>(
    initBetMethodData(initBetTypeData(1)),
  );
  const [betTypeList, setBetTypeList] = useState<BetTypeList>(
    initBetTypeListData(),
  );
  const [wheelAxisList, setWheelAxisList] = useState<WheelAxisList>(
    initWheelAxisList(),
  );
  const [isMultiData, setIsMultiData] = useState<IsMultiData>({
    [BetMethod.Formation]: false,
    [BetMethod.Wheel]: initBetTypeData(false),
  });
  const [orderSingleraceData, setOrderSingleraceData] = useState<OrderData>(
    initBetTypeData([]),
  );
  const [orderStep, setOrderStep] = useState<OrderStep>(OrderStep.Ready);
  const [isOrderProcessing, setIsOrderProcessing] = useState<boolean>(false);
  // 投票可能なレースであるかどうか
  const [isBettable, setIsBettable] = useState<boolean>(
    initIsBettable(race, raceProgram),
  );
  // SPのナビ操作の表示切り替え用
  const [isBetting, setIsBetting] = useState<boolean>(
    initIsBettable(race, raceProgram) && !hasInfoType,
  );
  const [entryRacerNoteList, setEntryRacerNoteList] = useState<
    EntryRacerNote[]
  >([]);
  const [targetEntryRacer, setTargetEntryRacer] = useState<EntryRacer | null>(
    null,
  );
  const [isOpenBetPanel, setIsOpenBetPanel] = useModal(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useModal(false);
  const [isMemoModalOpen, setIsMemoModalOpen] = useModal(false);
  const [isGuestModalOpen, setIsGuestModalOpen] = useModal(false);

  const gtaScript = useScript('/customGtaScript.js?20241120');

  const [orderSingleRaceMutation, orderSingleRaceMutationResult] =
    useOrderSingleRaceMutation();

  // 投票関連の各ステートの初期化
  const betStateReset = () => {
    setFirstThreeRacerData(prev => {
      const newList = { ...prev };
      (Object.keys(newList) as (keyof FirstThreeRacerData)[]).forEach(key => {
        if (key === BetMethod.Wheel) {
          newList[key] = initBetTypeData({ ...initWheelRacerList });
        } else {
          newList[key] = [[], [], []];
        }
      });
      return newList;
    });
    setBettableData(initBetMethodData(initBetTypeData<BettableItem[]>([])));
    setBetCountData(initBetMethodData(initBetTypeData(1)));
    setIsMultiData({
      [BetMethod.Wheel]: initBetTypeData(false),
      [BetMethod.Formation]: false,
    });
  };

  const updateBetPanelTicketCount = () => {
    let ticketCount = 0;
    if (betDataList[race.id] && !raceSelector(race).isCanceled) {
      Object.keys(betDataList[race.id]).forEach(uuid => {
        ticketCount += betDataList[race.id][uuid].tickets.filter(
          t => !t.isRemoved && t.count > 0,
        ).length;
      });
    }
    setBetPanelTicketCount(ticketCount);
  };

  const addToBetPanel = () => {
    const betData = {} as BetData;
    const newBetData = { ...betDataList };
    // 購入可能で且つ口数が1以上に設定されている組番のみを抽出する
    (Object.keys(bettableData) as BetMethod[]).forEach(methodKey => {
      (Object.keys(bettableData[methodKey]) as BetType[]).forEach(typeKey => {
        const tickets: Ticket[] = bettableData[methodKey][typeKey]
          .map(item => {
            // 修正前後で同じ買い目が存在する場合は、口数を修正前の口数で上書きする
            if (modifyBetDetailKey) {
              const target = newBetData[race.id][
                modifyBetDetailKey
              ].tickets.find(t => t.setNum === item.setNum && t.count > 1);
              if (target) {
                return { ...item, count: target.count };
              }
            }
            return {
              setNum: item.setNum,
              count: betCountData[methodKey][typeKey],
              isRemoved: item.isRemoved,
            } as Ticket;
          })
          .filter(ticket => ticket.count > 0);
        if (tickets.length) {
          const isMulti = () => {
            if (methodKey === BetMethod.Formation) {
              return isMultiData[methodKey];
            }
            if (methodKey === BetMethod.Wheel) {
              return isMultiData[methodKey][typeKey];
            }
            return false;
          };
          const firstThreeRacerList = () => {
            if (
              methodKey === BetMethod.Formation ||
              methodKey === BetMethod.Box
            ) {
              return firstThreeRacerData[methodKey];
            }
            if (methodKey === BetMethod.Wheel) {
              return firstThreeRacerData[methodKey][typeKey][
                wheelAxisList[typeKey]
              ];
            }
            return [[], [], []] as FirstThreeRacerList;
          };
          const wheelAxis = () => {
            if (methodKey === BetMethod.Wheel) {
              return wheelAxisList[typeKey];
            }
            return undefined;
          };
          betData[uuidv4()] = {
            betMethod: methodKey,
            betType: typeKey,
            tickets,
            isMulti: isMulti(),
            firstThreeRacerList: firstThreeRacerList(),
            wheelAxis: wheelAxis(),
            orderDeadline: race.orderDeadline,
          };
        }
      });
    });
    newBetData[race.id] = Object.assign(newBetData[race.id] || {}, betData);
    setBetDataList(newBetData);

    // 修正中の買い目がある場合は削除する
    if (modifyBetDetailKey) {
      setModifyBetDetailKey('');
      setBetDataList(prev => {
        const newList = { ...prev };
        delete newList[race.id][modifyBetDetailKey];
        return newList;
      });
      toast.success('修正が完了しました');
    } else {
      toast.success('投票パネルに追加されました');
    }

    // 投票関連の各ステートの初期化
    betStateReset();
  };

  const cancelModifyTheBetDetail = () => {
    const modifyingBetMethod =
      betDataList[race.id][modifyBetDetailKey].betMethod;
    if (modifyingBetMethod !== BetMethod.Odds) {
      setFirstThreeRacerData(prev => {
        const newData = { ...prev };
        if (modifyingBetMethod === BetMethod.Wheel) {
          newData[modifyingBetMethod] = initBetTypeData(initWheelRacerList);
        } else {
          newData[modifyingBetMethod] = [[], [], []];
        }
        return newData;
      });
    }

    setBettableData(prev => {
      const newData = { ...prev };
      newData[modifyingBetMethod] = { ...initBetTypeData<BettableItem[]>([]) };
      return newData;
    });

    toast.success('修正を取り消しました');

    setModifyBetDetailKey('');
  };

  const modifyTheBetDetail = (betDetail: BetDetail, key: string) => {
    // 投票関連の各ステートの初期化
    betStateReset();

    // 修正中だったBetMethodで選択している状態を初期化する
    if (modifyBetDetailKey) {
      cancelModifyTheBetDetail();
    }

    setIsBetting(true);
    setIsOpenBetPanel(false);
    setModifyBetDetailKey(key);
    setBetMethod(betDetail.betMethod);
    setBetTypeList(prev => {
      const newList = { ...prev };
      if (
        betDetail.betMethod === BetMethod.Formation ||
        betDetail.betMethod === BetMethod.Box
      ) {
        newList[betDetail.betMethod] = [betDetail.betType];
      } else {
        newList[betDetail.betMethod] = betDetail.betType;
      }
      return newList;
    });

    // 買い目情報を投票シートに反映する
    if (betDetail.betMethod === BetMethod.Odds) {
      setBettableData(prev => {
        const newData = { ...prev };
        newData[BetMethod.Odds][betDetail.betType] = betDetail.tickets.map(
          t => ({
            setNum: t.setNum,
            isRemoved: t.isRemoved,
          }),
        );
        return newData;
      });
      setOddsListType(OddsListType.Table);
    } else {
      if (betDetail.betMethod === BetMethod.Wheel) {
        setWheelAxisList(prev => {
          const newData = { ...prev };
          if (betDetail.wheelAxis) {
            newData[betDetail.betType] = betDetail.wheelAxis;
          }
          return newData;
        });
      }
      setBettableData(prev => {
        const newData = { ...prev };
        newData[betDetail.betMethod][betDetail.betType] = betDetail.tickets.map(
          t => ({
            setNum: t.setNum,
            isRemoved: t.isRemoved,
          }),
        );
        return newData;
      });
      setFirstThreeRacerData(prev => {
        const newData = { ...prev };
        if (betDetail.betMethod === BetMethod.Wheel) {
          newData[BetMethod.Wheel][betDetail.betType][
            betDetail.wheelAxis as WheelAxis
          ] = betDetail.firstThreeRacerList;
        } else if (betDetail.betMethod !== BetMethod.Odds) {
          newData[betDetail.betMethod] = betDetail.firstThreeRacerList;
        }
        return newData;
      });
      setIsMultiData(prev => {
        const newData = { ...prev };
        if (betDetail.betMethod === BetMethod.Wheel) {
          newData[BetMethod.Wheel][betDetail.betType] = betDetail.isMulti;
        }
        if (betDetail.betMethod === BetMethod.Formation) {
          newData[BetMethod.Formation] = betDetail.isMulti;
        }
        return newData;
      });
    }
  };

  const orderRace = (
    input: OrderSingleRaceInput,
    userDevice: string,
  ): Promise<void> => {
    setIsOrderProcessing(true);

    return orderSingleRaceMutation({
      variables: input,
      context: {
        headers: {
          'X-User-Device': userDevice,
        },
      },
    })
      .then(res => {
        if (res.data?.orderSingleRace.status === OrderStatus.Success) {
          setOrderStep(OrderStep.Complete);
          setBetDataList(prev => {
            const newData = { ...prev };
            delete newData[race.id];
            return newData;
          });
          try {
            sendI3ToretaUseEvent(
              res.data!.orderSingleRace.emoneyTransactionId!,
              serviceCode,
            );

            if (gtaScript === Status.Ready) {
              window.sendGtmPurchaseComplete(
                res.data!.orderSingleRace.id,
                res.data!.orderSingleRace.totalAmount,
                formatPurchaseProductDetail(
                  raceProgram.velodromeCode,
                  race.raceNum,
                  res.data!.orderSingleRace.details as OrderSingleRaceDetail[],
                ),
              );
            }
          } catch (error) {
            // GTMタグに関してはエラーを検知しないようにする
          }
          orderSingleracesRefetch();
          toast.success('投票が完了しました');
          return Promise.resolve();
        }
        return Promise.reject(new Error('購入に失敗しました。'));
      })
      .catch(() => {
        setIsOpenErrorModal(true);
        raceQueryRefetch();
        setOrderStep(OrderStep.Confirm);
      })
      .finally(() => {
        if (walletQueryRefetch) {
          walletQueryRefetch();
        }
        setIsOrderProcessing(false);
      });
  };

  const isModifyingDisabled = (
    key: 'betMethod' | 'betType' | 'wheelAxis',
    arg: string,
  ) => {
    if (
      !(
        modifyBetDetailKey &&
        betDataList[race.id] &&
        betDataList[race.id][modifyBetDetailKey]
      )
    ) {
      return false;
    }
    return arg !== betDataList[race.id][modifyBetDetailKey][key];
  };

  const onOrderClosed = () => {
    betStateReset();
    setIsBettable(false);
    setIsBetting(false);
    setModifyBetDetailKey('');
    if (betMethod === BetMethod.Odds) {
      setRaceInfoType(RaceInfoType.Odds);
      setBetMethod(BetMethod.Formation);
    }
    /**
     * 締切と同タイミングで投票された場合に投票データが先にnullになってしまう場合があるので
     * 投票締切後10秒経過で投票データを削除する
     */
    setTimeout(() => {
      setBetDataList(prev => {
        const newData = { ...prev };
        delete newData[race.id];
        return newData;
      });
    }, 10 * 1000);
  };

  const showEntryRacerNoteModal = (entryRacer: EntryRacer) => {
    setTargetEntryRacer(entryRacer);
    setIsMemoModalOpen(true);
  };

  const velodrome = useMemo(() => {
    return getVelodromeByCode(raceProgram.velodromeCode);
  }, [raceProgram.velodromeCode]);

  const isCanceled = raceProgram.isCanceled || raceSelector(race).isCanceled;

  const isFirstDay = useMemo(
    () => raceProgram.heldDate.isSame(raceProgram.heldFirstDate, 'day'),
    [raceProgram],
  );

  const isNotUnregisteredRaces = useMemo(
    () =>
      raceProgram.races.every(
        r => r.raceEntryStatus !== RaceEntryStatus.Unregistered,
      ),
    [raceProgram],
  );

  const hasOrderTicket = useMemo(
    () => Object.values(orderSingleraceData).some(od => od.length > 0),
    [orderSingleraceData],
  );

  useEffect(() => {
    setOrderSingleraceData(parsedOrderData(orderSingleraces));
  }, [orderSingleraces]);

  useEffect(() => {
    if (!isBettable) {
      setBetMethod(BetMethod.Formation);
    }

    // ログインしていない場合はモーダルを表示、一度表示済みの場合は表示しない
    const viewedGuestModal = ViewedGuestModalSessionStorage;
    if (viewedGuestModal.get() == null || viewedGuestModal.get() == false) {
      setIsGuestModalOpen(isGuest && isBettable);
    }

    // 投票可能な場合は人気順をデフォルト表示する
    if (isBettable) {
      setOddsListType(OddsListType.Popularity);
    } else {
      setOddsListType(OddsListType.Table);
    }
  }, [isBettable]);

  // URLが変わった場合に初期化する
  useEffect(() => {
    betStateReset();
    setIsOpenBetPanel(false);
    setModifyBetDetailKey('');
    setBetTypeList(initBetTypeListData());
    setFirstThreeRacerData(initFirstThreeRacerData());
    setWheelAxisList(initWheelAxisList());
    setIsBettable(initIsBettable(race, raceProgram));
    setIsBetting(initIsBettable(race, raceProgram) && !hasInfoType);
    updateBetPanelTicketCount();
    // URLクエリパラメータからどの画面を表示するか判別
    setBetMethod(BetMethod.Formation);
    setRaceInfoType(RaceInfoType.Basic);
    // eslint-disable-next-line default-case
    switch (raceInfoType) {
      case RaceInfoType.Result:
        if (race.singleRaceRefunds.length) {
          setRaceInfoType(RaceInfoType.Result);
        }
        break;
      case RaceInfoType.Odds:
        if (isBettable) {
          setBetMethod(BetMethod.Odds);
        } else {
          setRaceInfoType(RaceInfoType.Odds);
        }
        break;
      case RaceInfoType.Order:
      case RaceInfoType.RecentResult:
      case RaceInfoType.VelodromeResult:
      case RaceInfoType.Line:
        setRaceInfoType(raceInfoType);
        break;
    }
  }, [race.id]);

  // 投票シートに関連するデータに変更が加わった際に購入可能件数を更新する処理
  useEffect(() => {
    if (betMethod === BetMethod.Odds) {
      return;
    }

    const betType = betTypeList[betMethod] as BetType;

    const isMulti = () => {
      if (betMethod === BetMethod.Formation) return isMultiData[betMethod];
      if (betMethod === BetMethod.Wheel) return isMultiData[betMethod][betType];
      return false;
    };
    const firstThreeRacerList = () => {
      if (betMethod === BetMethod.Wheel) {
        return firstThreeRacerData[betMethod][betType][wheelAxisList[betType]];
      }
      return firstThreeRacerData[betMethod];
    };

    const list = makeBettableList(isMulti(), ...firstThreeRacerList());

    setBettableData(prev => {
      const newData = { ...prev };

      if (betMethod === BetMethod.Wheel) {
        newData[betMethod][betTypeList[betMethod]] = list[
          betTypeList[betMethod]
        ].map((item, i) => {
          const target = newData[betMethod][betTypeList[betMethod]].find(
            d => d.setNum === item.setNum,
          );
          if (target) {
            return target;
          }
          return list[betTypeList[betMethod]][i];
        });
      } else {
        // 選択されたbetTypeに関連する組番のみが表示されるようにフィルタリング
        (Object.keys(list) as BetType[]).forEach(key => {
          if (!betTypeList[betMethod].includes(key)) {
            list[key] = [];
          } else {
            list[key] = list[key].map((item, i) => {
              const target = newData[betMethod][key].find(
                d => d.setNum === item.setNum,
              );
              if (target) {
                return target;
              }
              return list[key][i];
            });
          }
        });
        newData[betMethod] = list;
      }

      return newData;
    });
  }, [firstThreeRacerData, betTypeList, wheelAxisList, isMultiData]);

  useEffect(() => {
    updateBetPanelTicketCount();
  }, [betDataList]);

  // 投票パネルに追加した買い目に含まれる車番が欠車になったときは欠車車番が含まれる買い目を削除する
  useEffect(() => {
    if (!betDataList[race.id]) return;

    const notReleasedEntryRacers = race.entryRacers
      .filter(x => x.releaseStatus !== ReleaseStatus.Released)
      .map(er => er.bicycleNum);

    if (notReleasedEntryRacers.length) {
      setBetDataList(prev =>
        filterInvalidBetData(race, prev, notReleasedEntryRacers),
      );
    }
  }, [race, betDataList]);

  useEffect(() => {
    if (entryRacerNotes) {
      setEntryRacerNoteList(entryRacerNotes);
    }
  }, [entryRacerNotes]);

  useEffect(() => {
    if (orderStep === OrderStep.Complete) {
      setOrderStep(OrderStep.Ready);
    }
  }, [isOpenBetPanel]);

  const layoutProps: LayoutProps = {
    ...props,
    racePrograms,
    betDataList,
    setBetDataList,
    betPanelTicketCount,
    modifyBetDetailKey,
    oddsListType,
    setOddsListType,
    bettableData,
    setBettableData,
    firstThreeRacerData,
    setFirstThreeRacerData,
    betCountData,
    setBetCountData,
    betTypeList,
    setBetTypeList,
    wheelAxisList,
    setWheelAxisList,
    isMultiData,
    setIsMultiData,
    isBettable,
    isCanceled,
    addToBetPanel,
    cancelModifyTheBetDetail,
    modifyTheBetDetail,
    isModifyingDisabled,
    onOrderClosed,
    isBetting,
    setIsBetting,
    entryRacerNoteList,
    setEntryRacerNoteList,
    isOpenBetPanel,
    setIsOpenBetPanel,
    targetEntryRacer,
    showEntryRacerNoteModal,
    isFirstDay,
    isNotUnregisteredRaces,
    velodrome,
    orderSingleraceData,
    hasOrderTicket,
    oddsQueryRefetch,
    isOrderProcessing,
  };

  return (
    <>
      {isBettable && (
        <Suspense fallback={<></>}>
          <SingleraceOverDeadlineWatcher
            orderDeadline={raceSelector(race).marginedOrderDeadline}
            isCanceled={isCanceled}
            onClosed={onOrderClosed}
          />
        </Suspense>
      )}
      <Suspense fallback={<></>}>
        <BetPanelModal
          race={race}
          oddsData={oddsData}
          raceProgram={raceProgram}
          racePrograms={racePrograms}
          isOpen={isOpenBetPanel}
          isProcessing={isOrderProcessing}
          handleClose={() => setIsOpenBetPanel(false)}
          betDataList={betDataList}
          setBetDataList={setBetDataList}
          modifyTheBetDetail={modifyTheBetDetail}
          modifyBetDetailKey={modifyBetDetailKey}
          cancelModifyTheBetDetail={cancelModifyTheBetDetail}
          orderStep={orderStep}
          setOrderStep={setOrderStep}
          orderRace={orderRace}
        />
      </Suspense>
      <Suspense fallback={<></>}>
        <EntryRacerNoteModal
          isOpen={isMemoModalOpen}
          setIsOpen={setIsMemoModalOpen}
          raceId={race.id}
          targetEntryRacer={targetEntryRacer}
          entryRacerNoteList={entryRacerNoteList}
          setEntryRacerNoteList={setEntryRacerNoteList}
        />
      </Suspense>
      <Suspense fallback={<></>}>
        <GuestModal
          isOpen={isGuestModalOpen}
          handleClose={() => setIsGuestModalOpen(false)}
        />
      </Suspense>
      <Suspense fallback={<></>}>
        <ErrorModal
          isOpen={isOpenErrorModal}
          handleClose={() => setIsOpenErrorModal(false)}
          apolloError={orderSingleRaceMutationResult.error}
        />
      </Suspense>
      <Toaster />
      <Suspense fallback={<></>}>
        {isMobile ? (
          <SpLayout {...layoutProps} />
        ) : (
          <PcLayout {...layoutProps} />
        )}
      </Suspense>
    </>
  );
};

export default RaceTemplate;
